import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  FileText, Users, Linkedin, GraduationCap, Sparkles, ArrowRight,
  CheckCircle, Star, Phone, Mail, Menu, X, ChevronRight, Calendar,
  MessageSquare, Shield, Target, Award, TrendingUp, Facebook
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function RevealSection({ children, className = '' }) {
  const ref = useReveal();
  return <div ref={ref} className={`reveal-section ${className}`}>{children}</div>;
}

// --- Navigation ---
function Navigation({ onBooking }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = useCallback((id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const links = [
    { label: 'About', id: 'about' },
    { label: 'Services', id: 'services' },
    { label: 'Process', id: 'process' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <nav data-testid="main-navigation" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass py-3' : 'py-5 bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid="logo-link">
          <img src="https://customer-assets.emergentagent.com/job_profile-boost-56/artifacts/9e4tf1j3_Leading%20Presence%20Logo.png" alt="Leading Presence" className="h-10 w-auto" />
        </button>

        <div className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <button key={l.id} onClick={() => scrollTo(l.id)} className="text-slate-500 hover:text-slate-900 text-sm font-medium transition-colors" data-testid={`nav-${l.id}`}>
              {l.label}
            </button>
          ))}
          <button onClick={onBooking} className="btn-blue px-5 py-2.5 rounded-full text-sm" data-testid="nav-book-btn">
            Book Consultation
          </button>
        </div>

        <button className="md:hidden text-slate-900" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white mt-2 mx-4 rounded-xl p-6 animate-fade-in shadow-lg border border-slate-100">
          {links.map(l => (
            <button key={l.id} onClick={() => scrollTo(l.id)} className="block w-full text-left py-3 text-slate-500 hover:text-slate-900 text-base font-medium transition-colors border-b border-slate-100" data-testid={`mobile-nav-${l.id}`}>
              {l.label}
            </button>
          ))}
          <button onClick={() => { setMobileOpen(false); onBooking(); }} className="btn-blue w-full mt-4 py-3 rounded-full text-sm" data-testid="mobile-book-btn">
            Book Consultation
          </button>
        </div>
      )}
    </nav>
  );
}

// --- Hero ---
function Hero({ onBooking }) {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/50 to-white">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-blue-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-50/60 rounded-full blur-3xl" />
      <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-blue-200/20 rounded-full blur-2xl" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-12 text-center">
        <div className="animate-fade-up">
          <span className="inline-block text-sm uppercase tracking-[0.3em] font-bold text-blue-600 mb-6">Career Development & Consulting</span>
          <h1 className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter text-slate-900 mb-6 leading-[1.05]">
            Build Your Presence.<br />
            <span className="blue-text">Unlock Your Future.</span>
          </h1>
          <p className="text-slate-500 text-lg sm:text-xl max-w-2xl mx-auto mb-6 leading-relaxed">
            I help international and domestic students choose the right Canadian programs based on real HR hiring insights — so you don't waste time and money.
          </p>
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100">
              <span className="text-blue-600 font-bold text-sm">CPHR</span>
              <span className="text-slate-500 text-xs">20+ Years HR Experience</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={onBooking} className="btn-blue px-8 py-4 rounded-full text-base flex items-center justify-center gap-2 animate-pulse-blue" data-testid="hero-book-btn">
              <Calendar size={18} />
              Book Free Consultation
            </button>
            <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 rounded-full border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-600 transition-all text-base flex items-center justify-center gap-2 bg-white" data-testid="hero-explore-btn">
              Explore Services
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- About ---
function About() {
  return (
    <section id="about" data-testid="about-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4 block">About Me</span>
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 mb-6">
                I've Been Where<br />You Are Now
              </h2>
              <p className="text-slate-500 text-base leading-relaxed mb-6">
                I'm a CPHR-certified career consultant with over 20 years of HR experience. I work directly with both international and domestic students navigating the complicated Canadian admissions process, as well as professionals looking to break into or advance in the job market. I help you choose the right programs based on real HR hiring insights — so you don't waste time and money on degrees that don't lead to jobs.
              </p>
              <p className="text-slate-500 text-base leading-relaxed mb-6">
                Having spent two decades on the hiring side, I know exactly what Canadian employers look for. I bring that insider perspective to every resume I write, every interview I coach, and every career strategy I build — personally, one-on-one, with you.
              </p>
              {/* CPHR Badge */}
              <div className="flex items-center gap-4 mb-8 p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-['Outfit'] font-bold text-lg">CPHR</span>
                </div>
                <div>
                  <div className="text-slate-900 font-semibold text-sm">Services Provided by CPHR</div>
                  <div className="text-slate-500 text-xs leading-relaxed">Chartered Professional in Human Resources — Canada's premier HR designation ensuring expert-level career guidance.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { num: '500+', label: 'Clients Helped' },
                  { num: '95%', label: 'Success Rate' },
                  { num: '50+', label: 'Industries Covered' },
                  { num: '20+', label: 'Years in HR' }
                ].map((s, i) => (
                  <div key={i} className="text-center p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="text-3xl font-['Outfit'] font-bold text-blue-600">{s.num}</div>
                    <div className="text-sm text-slate-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1641998148499-cb6b55a3c0d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBtb2Rlcm58ZW58MHx8fHwxNzc2MjQ0ODU5fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern office"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white border border-blue-100 rounded-xl p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                    <Award size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-slate-900 font-semibold text-sm">CPHR Certified</div>
                    <div className="text-slate-500 text-xs">Canadian HR Insider</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </RevealSection>
      </div>
    </section>
  );
}

// --- Services ---
const services = [
  { icon: FileText, title: 'CV & Resume Building', desc: 'I personally craft ATS-optimized, industry-specific resumes that get you past screening algorithms and into interviews. No templates — every resume is built from scratch.' },
  { icon: Users, title: 'Interview Coaching', desc: 'I sit with you one-on-one for mock interviews, give you honest feedback, and build your confidence until you walk into every interview knowing you belong there.' },
  { icon: Linkedin, title: 'LinkedIn & Portfolio', desc: 'I optimize your LinkedIn profile and build your portfolio so recruiters find you — not the other way around. Real strategy, not generic tips.' },
  { icon: GraduationCap, title: 'Education Consulting', desc: 'I guide both international and domestic students through the complicated Canadian admissions process — helping you pick the right programs that actually lead to jobs.' },
  { icon: Sparkles, title: 'Personal Branding', desc: 'I help you build a personal brand that makes you unforgettable — from your digital presence to your elevator pitch. You\'ll stand out in any room.' },
];

function Services({ onBooking }) {
  return (
    <section id="services" data-testid="services-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4 block">What I Do</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 mb-4">
            How I Can Help You
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mx-auto">
            Everything I offer is hands-on, personalized, and built around your specific goals. No cookie-cutter packages.
          </p>
        </RevealSection>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <RevealSection key={i}>
              <div className="card-hover bg-white rounded-2xl p-8 h-full flex flex-col" data-testid={`service-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                  <s.icon size={24} className="text-blue-600" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-3">{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed flex-grow mb-6">{s.desc}</p>
                <button onClick={onBooking} className="text-blue-600 text-sm font-semibold flex items-center gap-2 hover:gap-3 transition-all" data-testid={`service-cta-${i}`}>
                  Get Started <ChevronRight size={16} />
                </button>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Process ---
const steps = [
  { num: '01', title: 'Free Consultation', desc: 'We start with a candid conversation. I learn about your goals, your background, and where you want to be — no pressure, just real talk.', icon: MessageSquare },
  { num: '02', title: 'Your Strategy', desc: 'I build a tailored action plan just for you — with clear steps, realistic timelines, and outcomes you can measure.', icon: Target },
  { num: '03', title: 'Profile Development', desc: 'I get to work on your professional assets — your resume, LinkedIn, portfolio, and personal brand. Everything polished to perfection.', icon: TrendingUp },
  { num: '04', title: 'Launch & Support', desc: 'When you\'re ready, I guide you through applications, interviews, and negotiations. I don\'t disappear — I\'m with you until you land it.', icon: Award },
];

function Process() {
  return (
    <section id="process" data-testid="process-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-white relative">
      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-20">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4 block">How It Works</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 mb-4">
            Four Steps to Your Success
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mx-auto">
            A straightforward process I've refined working with hundreds of clients just like you.
          </p>
        </RevealSection>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            {steps.map((step, i) => (
              <RevealSection key={i}>
                <div className="flex gap-6 group" data-testid={`process-step-${i}`}>
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200 group-hover:border-blue-300 group-hover:bg-blue-50 transition-all flex items-center justify-center">
                      <step.icon size={24} className="text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-blue-600 font-['Outfit'] font-bold text-sm">{step.num}</span>
                      <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900">{step.title}</h3>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb25zdWx0YXRpb24lMjBkZXNrJTIwbGFwdG9wJTIwYnVzaW5lc3MlMjBzdHJhdGVneXxlbnwwfHx8fDE3NzYyNDU3ODh8MA&ixlib=rb-4.1.0&q=85"
                alt="Career consultation"
                className="w-full max-w-md mx-auto rounded-2xl border border-slate-200 shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Why Choose Me ---
const reasons = [
  { icon: Shield, title: 'Real HR Experience', desc: 'With 20+ years on the hiring side of the table, I know what employers actually look for — not just what career blogs say.' },
  { icon: TrendingUp, title: '95% Success Rate', desc: '95% of my clients secure interviews within 30 days. I don\'t make promises I can\'t keep.' },
  { icon: Users, title: 'It\'s Just You & Me', desc: 'No team of juniors. No outsourcing. When you work with me, you get me — every session, every revision.' },
  { icon: Award, title: 'I Stay Until You Land It', desc: 'From our first call to your offer letter, I\'m in your corner. I don\'t ghost after the resume is done.' },
];

function WhyChooseUs() {
  return (
    <section data-testid="why-choose-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4 block">Why Work With Me</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 mb-4">
            What Makes This Different
          </h2>
        </RevealSection>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <RevealSection key={i}>
              <div className="card-hover bg-white rounded-2xl p-8 text-center h-full" data-testid={`reason-card-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-6">
                  <r.icon size={28} className="text-blue-600" />
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-3">{r.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{r.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Testimonials ---
function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(() => {});
  }, []);

  if (testimonials.length === 0) return null;

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <RevealSection className="text-center mb-16">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4 block">Testimonials</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 mb-4">
            Success Stories
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mx-auto">
            Don't take my word for it — here's what my clients have to say.
          </p>
        </RevealSection>
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((t, i) => (
            <RevealSection key={t.id || i}>
              <div className="card-hover bg-white rounded-2xl p-8 h-full" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating || 5 }).map((_, j) => (
                    <Star key={j} size={16} className="text-blue-500 fill-blue-500" />
                  ))}
                </div>
                <p className="text-slate-700 text-base leading-relaxed mb-6 italic">"{t.content}"</p>
                <Separator className="mb-4 bg-slate-100" />
                <div className="flex items-center gap-4">
                  {t.image_url && (
                    <img src={t.image_url} alt={t.name} className="w-12 h-12 rounded-full object-cover border-2 border-blue-100" />
                  )}
                  <div>
                    <div className="text-slate-900 font-semibold text-sm">{t.name}</div>
                    <div className="text-slate-500 text-xs">{t.role}{t.company ? ` at ${t.company}` : ''}</div>
                  </div>
                </div>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- CTA ---
function CTASection({ onBooking }) {
  return (
    <section data-testid="cta-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-slate-50">
      <RevealSection>
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 sm:p-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
          <div className="relative z-10">
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-white mb-4">
              Your next opportunity<br />starts here.
            </h2>
            <p className="text-blue-100 text-base max-w-xl mx-auto mb-8">
              I've helped hundreds of international students and professionals land the career they deserve. Let me do the same for you.
            </p>
            <button onClick={onBooking} className="bg-white text-blue-600 font-semibold px-10 py-4 rounded-full text-base flex items-center justify-center gap-2 mx-auto hover:bg-blue-50 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid="cta-book-btn">
              <Calendar size={18} />
              Book Your Session Now
            </button>
          </div>
        </div>
      </RevealSection>
    </section>
  );
}

// --- Contact ---
function Contact() {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', service: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) { toast.error('Please fill in required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success('Message sent! I\'ll get back to you within 24 hours.');
      setForm({ full_name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 px-6 sm:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          <RevealSection>
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-blue-600 mb-4 block">Get In Touch</span>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight font-medium text-slate-900 mb-6">
              Let's Talk About Your Goals
            </h2>
            <p className="text-slate-500 text-base leading-relaxed mb-10">
              Ready to take the next step? Fill out the form and I'll personally reach out within 24 hours to schedule your free consultation. No sales pitch — just an honest conversation about where you are and how I can help.
            </p>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-blue-600" />
                </div>
                <div>
                  <div className="text-slate-900 font-semibold text-sm">Email</div>
                  <a href="mailto:leadingpresence@gmail.com" className="text-slate-500 text-sm hover:text-blue-600 transition-colors">leadingpresence@gmail.com</a>
                </div>
              </div>
            </div>
          </RevealSection>

          <RevealSection>
            <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 space-y-5" data-testid="contact-form">
              <div>
                <Label className="text-slate-900 text-sm mb-2 block">Full Name *</Label>
                <Input value={form.full_name} onChange={e => setForm(p => ({...p, full_name: e.target.value}))} placeholder="John Doe" className="bg-white border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11" data-testid="contact-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-slate-900 text-sm mb-2 block">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="john@email.com" className="bg-white border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11" data-testid="contact-email" />
                </div>
                <div>
                  <Label className="text-slate-900 text-sm mb-2 block">Phone</Label>
                  <Input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+1 (555) 000-0000" className="bg-white border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11" data-testid="contact-phone" />
                </div>
              </div>
              <div>
                <Label className="text-slate-900 text-sm mb-2 block">Service Interested In</Label>
                <Select value={form.service} onValueChange={v => setForm(p => ({...p, service: v}))}>
                  <SelectTrigger className="bg-white border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 h-11" data-testid="contact-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="cv-resume">CV & Resume Building</SelectItem>
                    <SelectItem value="interview-coaching">Interview Coaching</SelectItem>
                    <SelectItem value="linkedin-portfolio">LinkedIn & Portfolio</SelectItem>
                    <SelectItem value="education-consulting">Education Consulting</SelectItem>
                    <SelectItem value="personal-branding">Personal Branding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-900 text-sm mb-2 block">Message</Label>
                <Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Tell me about your goals..." rows={4} className="bg-white border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 resize-none" data-testid="contact-message" />
              </div>
              <button type="submit" disabled={submitting} className="btn-blue w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit-btn">
                {submitting ? 'Sending...' : 'Send Message'}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </form>
          </RevealSection>
        </div>
      </div>
    </section>
  );
}

// --- Footer ---
function Footer() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer data-testid="footer" className="py-16 px-6 sm:px-12 border-t border-slate-100 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="https://customer-assets.emergentagent.com/job_profile-boost-56/artifacts/9e4tf1j3_Leading%20Presence%20Logo.png" alt="Leading Presence" className="h-12 w-auto" />
            </div>
            <p className="text-slate-500 text-sm leading-relaxed mb-3">Helping international and domestic students build careers that actually work — with 20+ years of real HR insight and personal guidance.</p>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-100 w-fit">
              <span className="text-blue-600 font-bold text-xs">CPHR</span>
              <span className="text-slate-500 text-xs">Certified</span>
            </div>
          </div>
          <div>
            <h4 className="text-slate-900 font-['Outfit'] font-semibold mb-4">Quick Links</h4>
            <div className="space-y-2">
              {['about', 'services', 'process', 'testimonials', 'contact'].map(id => (
                <button key={id} onClick={() => scrollTo(id)} className="block text-slate-500 text-sm hover:text-blue-600 transition-colors capitalize">{id}</button>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-slate-900 font-['Outfit'] font-semibold mb-4">Services</h4>
            <div className="space-y-2 text-sm text-slate-500">
              <p>CV & Resume Building</p>
              <p>Interview Coaching</p>
              <p>LinkedIn Optimization</p>
              <p>Education Consulting</p>
              <p>Personal Branding</p>
            </div>
          </div>
          <div>
            <h4 className="text-slate-900 font-['Outfit'] font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-sm text-slate-500">
              <p>leadingpresence@gmail.com</p>
            </div>
            <div className="flex gap-3 mt-4">
              <a href="https://www.facebook.com/theleadingpresence" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-blue-400 hover:text-blue-600 transition-all text-slate-500" data-testid="footer-facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>
        </div>
        <Separator className="bg-slate-200 mb-6" />
        <div className="text-center text-slate-400 text-xs">
          &copy; {new Date().getFullYear()} Leading Presence. All rights reserved.
          <span className="mx-2">|</span>
          Made by <a href="https://www.zazenmediagroup.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 transition-colors">Zazen Media Group</a>
        </div>
      </div>
    </footer>
  );
}

// --- Booking Modal ---
function BookingModal({ open, onClose }) {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', service: '', preferred_date: '', preferred_time: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.service) { toast.error('Please fill in required fields'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success('Booked! I\'ll confirm your time shortly.');
      setForm({ full_name: '', email: '', phone: '', service: '', preferred_date: '', preferred_time: '', message: '' });
      onClose();
    } catch {
      toast.error('Failed to book. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-900 font-['Outfit'] text-2xl">Book a Free Chat With Me</DialogTitle>
          <DialogDescription className="text-slate-500">Pick a time that works for you. I'll confirm within 24 hours.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-900 text-sm mb-1.5 block">Full Name *</Label>
            <Input value={form.full_name} onChange={e => setForm(p => ({...p, full_name: e.target.value}))} placeholder="Your full name" className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11" data-testid="booking-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-900 text-sm mb-1.5 block">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="you@email.com" className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11" data-testid="booking-email" />
            </div>
            <div>
              <Label className="text-slate-900 text-sm mb-1.5 block">Phone</Label>
              <Input type="tel" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+1 (555) 000" className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 h-11" data-testid="booking-phone" />
            </div>
          </div>
          <div>
            <Label className="text-slate-900 text-sm mb-1.5 block">Service *</Label>
            <Select value={form.service} onValueChange={v => setForm(p => ({...p, service: v}))}>
              <SelectTrigger className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 h-11" data-testid="booking-service">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="cv-resume">CV & Resume Building</SelectItem>
                <SelectItem value="interview-coaching">Interview Coaching</SelectItem>
                <SelectItem value="linkedin-portfolio">LinkedIn & Portfolio</SelectItem>
                <SelectItem value="education-consulting">Education Consulting</SelectItem>
                <SelectItem value="personal-branding">Personal Branding</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-900 text-sm mb-1.5 block">Preferred Date</Label>
              <Input type="date" value={form.preferred_date} onChange={e => setForm(p => ({...p, preferred_date: e.target.value}))} className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 h-11" data-testid="booking-date" />
            </div>
            <div>
              <Label className="text-slate-900 text-sm mb-1.5 block">Preferred Time</Label>
              <Select value={form.preferred_time} onValueChange={v => setForm(p => ({...p, preferred_time: v}))}>
                <SelectTrigger className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 h-11" data-testid="booking-time">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-slate-900 text-sm mb-1.5 block">Additional Notes</Label>
            <Textarea value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Any specific topics you'd like to discuss..." rows={3} className="bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900 placeholder:text-slate-400 resize-none" data-testid="booking-message" />
          </div>
          <button type="submit" disabled={submitting} className="btn-blue w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50 mt-2" data-testid="booking-submit-btn">
            {submitting ? 'Booking...' : 'Confirm Booking'}
            {!submitting && <CheckCircle size={16} />}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// --- Main Landing Page ---
export default function LandingPage() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navigation onBooking={openBooking} />
      <Hero onBooking={openBooking} />
      <About />
      <Services onBooking={openBooking} />
      <Process />
      <WhyChooseUs />
      <Testimonials />
      <CTASection onBooking={openBooking} />
      <Contact />
      <Footer />
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
